import { AppSumoTier, BillingOption, FeatureActionStringMap, FeatureDescriptionStringMap, FeatureImageMap, FeatureTierLevel, FeatureTierLevelMap, FeatureTierMonthlyPriceMap, FeatureType, KlavioEventPropertyKey, KlayvioEvents, SpecialTiers, StandardProAddOns, TierItemMap, billingOptionForSubscription } from '../types';
import { GoogleTagEvent, logEventWithKlayvio, logWithAmplitude, logWithGoogleTagAndTopicOrganization } from '../helpers/api/api';
import { LitElement, css, html, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { isWorkingHours, openChatWindow } from '../helpers/intercom';

import { CartComponent } from './cart-component';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { capitalizeFirstLetter } from '../helpers/strings';
import { isDefined } from '../helpers/utils';
import { renderNonprofitDiscountNote } from '../helpers/nonprofit-note';
import organizationInfo from '../helpers/state/organization-info';
import { palette } from '../style/variables';
import subscriptionInfo from '../helpers/state/subscription-info';
import topicData from '../helpers/state/topic-data';

@customElement('upgrade-dialog')
export class UpgradeDialogElement extends LitElement {
    static amplitude_callsite: string = 'Upgrade Dialog';

    @query('#cart-component')
    cartComponent!: CartComponent;

    @property({ type: String })
    billingFrequency!: BillingOption;

    @property({ type: Boolean })
    showUseCase: boolean = false;

    @property({ type: String })
    currentTier?: FeatureTierLevel | SpecialTiers;

    @state()
    tier: FeatureTierLevel = FeatureTierLevel.ProBase;

    @state()
    addon?: StandardProAddOns;

    @state()
    annualSavings: number = 0;

    @state()
    quarterlySavings: number = 0;

    @state()
    showAboutFeature: boolean = !isDefined(this.featureType);

    @state()
    featureType?: FeatureType;

    @state()
    showHeader: boolean = true;

    @state()
    isCurrentSubsciber: boolean = false;

    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        const upgradeDialog = this.shadowRoot?.querySelector('.upgradeDialogContent') as HTMLElement | null;

        if (upgradeDialog) {
            const clickListener = (e: MouseEvent) => {
                if (isDefined(this.featureType)) {
                    logWithAmplitude(UpgradeDialogElement.amplitude_callsite, `Feature: ${this.featureType}`, {
                        upgrade_feature: this.featureType,
                    });
                    logEventWithKlayvio(KlayvioEvents.UpgradeModalOpened, accountInfo.email, [
                        { key: KlavioEventPropertyKey.MapId, value: topicData.topic?._id.$oid ?? '' },
                        { key: KlavioEventPropertyKey.UpgradeFeature, value: this.featureType },
                    ]);
                    logWithGoogleTagAndTopicOrganization(UpgradeDialogElement.amplitude_callsite, GoogleTagEvent.UPGRADE_CLICK, {
                        upgrade_feature: this.featureType,
                        upgrade_tier: this.tier,
                    });
                    upgradeDialog.removeEventListener('mouseenter', clickListener);
                } else if (isDefined(this.tier)) {
                    logWithAmplitude(UpgradeDialogElement.amplitude_callsite, `Tier: ${this.tier}`, {
                        upgrade_tier: this.tier,
                    });
                    logEventWithKlayvio(KlayvioEvents.UpgradeModalOpened, accountInfo.email, [
                        { key: KlavioEventPropertyKey.MapId, value: topicData.topic?._id.$oid ?? '' },
                        { key: KlavioEventPropertyKey.UpgradeTier, value: this.tier }
                    ]);
                    logWithGoogleTagAndTopicOrganization(UpgradeDialogElement.amplitude_callsite, GoogleTagEvent.UPGRADE_CLICK, {
                        upgrade_tier: this.tier,
                    });
                    upgradeDialog.removeEventListener('mouseenter', clickListener);
                }
            };

            upgradeDialog.addEventListener('mouseenter', clickListener);
        }
        await this.startup();
        this.isCurrentSubsciber = isDefined(subscriptionInfo.subscription_id) && subscriptionInfo.subscription_id !== '';
    }

    async startup() {
        this.billingFrequency = billingOptionForSubscription(subscriptionInfo.subscription?.billing_period, subscriptionInfo.subscription?.billing_period_unit);
        this.calculateSavings();
        if (this.featureType && isDefined(FeatureDescriptionStringMap[this.featureType])) {
            this.showAboutFeature = true;
        } else {
            this.showAboutFeature = false;
        }
        this.currentTier = TierItemMap[subscriptionInfo.subscription_plan_id ?? ''];
    }

    async updated(changedProperties: any) {
        // Listen for changes to tier and featureType and rerun firstUpdated
        changedProperties.forEach(async (oldValue: any, propName: string) => {
            if (propName === 'featureType') {
                await this.startup();
            }
            if (propName === 'addon' && isDefined(this.addon)) {
                await this.cartComponent?.addAddon(this.addon);
            }
        });
    }

    // Any feature not included in Pro is a Premium feature.
    get requiredPlanName(): string {
        if (isDefined(this.featureType) && !FeatureTierLevelMap[this.featureType].includes(FeatureTierLevel.ProBase)) {
            return 'Premium';
        }
        return 'Pro';
    }

    get headerText(): string {
        if (isDefined(this.featureType) && FeatureActionStringMap[this.featureType]) {
            return `${capitalizeFirstLetter(FeatureActionStringMap[this.featureType])} and more`;
        }
        return `Supercharge Your Map`;
    }

    private pricingClick() {
        logWithAmplitude(UpgradeDialogElement.amplitude_callsite, 'Proxi Pricing Click');
        window.open(`https://proxi.co/pricing`);
    }

    // calculate annaul savings vs monthly
    private calculateSavings() {
        const annualPrice = (FeatureTierMonthlyPriceMap[this.tier].Annual ?? 1)/12;
        const quarterlyPrice = (FeatureTierMonthlyPriceMap[this.tier].Quarterly ?? 1) / 3;
        const monthlyPrice = FeatureTierMonthlyPriceMap[this.tier].Monthly ?? 1;
        const annualsavings = Math.round((monthlyPrice - annualPrice) / monthlyPrice * 100);
        this.annualSavings = annualsavings;
        const quarterlysavings = Math.round((monthlyPrice - quarterlyPrice) / monthlyPrice * 100);
        this.quarterlySavings = quarterlysavings;
    }

    /** Charge for the selected frequency, plus its per-month equivalent and a label for the parenthetical. */
    private get selectedTierPricing() {
        const prices = FeatureTierMonthlyPriceMap[this.tier];
        if (this.billingFrequency === BillingOption.Annual) {
            const billedTotal = prices.Annual ?? 0;
            return { monthlyTotal: Math.round(billedTotal / 12), billedTotal, billedPeriodLabel: 'annually' };
        }
        if (this.billingFrequency === BillingOption.Quarterly) {
            const billedTotal = prices.Quarterly ?? 0;
            return { monthlyTotal: Math.round(billedTotal / 3), billedTotal, billedPeriodLabel: 'every 3 months' };
        }
        const billedTotal = prices.Monthly ?? 0;
        return { monthlyTotal: billedTotal, billedTotal, billedPeriodLabel: undefined };
    }

    private openAppSumo() {
        this.dispatchEvent(new CustomEvent('close'));
        openChatWindow(UpgradeDialogElement.amplitude_callsite, 'I am interested in upgrading to Proxi from my Appsumo Account');
    }

    render() {
        if (organizationInfo.isGuest) {
            return html`
                <h3 class="dialogTitle">
                    <b><i>${this.featureType ? capitalizeFirstLetter(FeatureActionStringMap[this.featureType]) : 'Get this feature'}</i></b> by Becoming a Teammate
                </h3>
                <div class="upgrade-dialog__content">
                    <div class="upgrade-dialog__description">
                        <p>
                            You do not have access to this feature as a guest. Please reach out to your organization's admin to upgrade your account.
                        </p>
                    </div>
                </div>
            `;
        }
        return html`
            ${accountInfo.isAppSumoUser === true && accountInfo.appSumoLicenseDetails?.appsumo_plan_id !== AppSumoTier.AppSumoFree ?
                html`
                <h3 class="dialogTitle">
                    <b><i>${this.featureType ? capitalizeFirstLetter(FeatureActionStringMap[this.featureType]) : 'Get this feature'}</i></b> by Upgrading To Proxi
                </h3>
                <div class="upgrade-dialog__content">
                    ${isDefined(this.featureType) && isDefined(FeatureImageMap[this.featureType]) ? html`
                        <div class="upgradeImage">
                            <img src="${FeatureImageMap[this.featureType]}" alt="Upgrade Image">
                        </div>
                    ` : ''}
                    <div class="upgrade-dialog__description">
                        <p>
                            You can upgrade to a Proxi plan unlock this feature.
                        </p>
                    </div>
                    <div class="actionButtonGroup">
                         <form-button class="button" type="submit" ?fullWidth=${true} @click="${this.openAppSumo}">
                            Reach Out Now
                        </form-button>
                    </div>
                </div>
            ` : html`
                <div class="upgradeDialogContent">
                    ${this.showHeader ? html`
                        <h3 class="dialogTitle">
                            ${this.headerText}
                            <br>
                            <span class="headerTier">
                            <i> with Proxi ${this.requiredPlanName}</i>
                            </span>
                        </h3>
                        <hr class="title-divider">
                    ` : ''}
                    <div class="bottom-area">
                        <div class="left-side-levels ${this.featureType && isDefined(FeatureDescriptionStringMap[this.featureType]) ? 'right-side-exists' : ''}">
                            <div class="upgrade-dialog__tiers">
                                <cart-component mini id="cart-component" @close="${() => this.dispatchEvent(new CustomEvent('close'))}"></cart-component>
                                </cart-component>
                                <div class="upgrade-dialog__demo">
                                    Need more info? <br>
                                    <b><a href="https://app.proxi.co/demo" target="_blank">Book a Demo</a></b>
                                    ${this.addon ? '' : html`
                                    |
                                    <b><a href="https://www.proxi.co/proxi-demo-video" target="_blank">Watch a Demo</a></b>
                                    `}
                                    ${isWorkingHours() ? html`
                                    |
                                    <b><span @click="${() => openChatWindow(UpgradeDialogElement.amplitude_callsite, `I am interested in Proxi learning more about Proxi ${this.tier}`)}" style="text-decoration:underline; cursor:pointer">Chat with Us</span></b>
                                    ` : ''}
                                </div>
                            </div>
                            ${this.currentTier !== this.tier ? html`
                                <div class="upgrade-dialog__subscribe">
                                    <span class="additional-info">
                                        ${this.tier === 'Starter' || this.isCurrentSubsciber ? '' : 'Cancel before the 7th day and you will not be charged.'}
                                        <br />
                                        ${renderNonprofitDiscountNote(UpgradeDialogElement.amplitude_callsite, {
                                            ...this.selectedTierPricing,
                                            hasExistingSub: this.isCurrentSubsciber,
                                        })}
                                    </span>
                                </div>
                            ` : ''}
                        </div>
                        ${this.featureType && isDefined(FeatureDescriptionStringMap[this.featureType]) ? html`
                            <div class="upgrade-dialog__content">
                                <div class="upgrade-dialog__description">
                                    <div class="included_text">
                                        <span class="included_area">Included with Proxi ${this.requiredPlanName}</span>
                                    </div>
                                    ${isDefined(FeatureImageMap[this.featureType]) ? html`
                                        <div class="upgradeImage">
                                            <img src="${FeatureImageMap[this.featureType]}" alt="Upgrade Image">
                                        </div>
                                    ` : ''}
                                    <p>
                                        ${FeatureDescriptionStringMap[this.featureType] ?? ''}
                                    </p>
                                </div>
                            </div>
                        ` : ''}
                    </div>
                </div>
            `}
        `;
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
                overflow: auto;
            }

            .upgradeDialogContent {
                max-height: 70vh;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                overflow: auto;
            }

            .dialogTitle {
                text-align: center;
                color: var(--main-primary-color);
                padding-left: 20px;
                padding-right: 20px;
                padding-bottom: 10px;
            }

            .bottom-area {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: stretch;
                flex: 1;
                gap: 5px;
            }

            h3 {
                margin-bottom: 0;
                margin-top: 0;
                line-height: 1.25rem;
            }

            .upgrade-dialog__tier-price {
                color: var(--main-success-color);
                font-weight: bold;
            }

            .headerTier {
                margin-top: 0;
                margin-bottom: 10px;
                font-size: 1rem;
            }

            .upgrade-dialog__tier_area {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 5px;
            }

            .upgrade-dialog__tier {
                border: 2px solid var(--main-success-color);
                cursor: pointer;
                padding: 5px;
                border-radius: 5px;
            }

            .savings, .additional-info {
                font-size: 0.6rem;
                line-height: 0.75rem;
                text-align: center;
                margin-top: 3px;
            }

            .upgrade-dialog__feature_description {
                display: flex;
                flex-direction: row;
                line-height: 1rem;
                gap: 5px;
                font-size: 0.75rem;
                max-height: 250px;
                overflow-x: auto;
                max-width: 48%;
                align-items: center;
            }

            .active_feature_true {
                background-color: var(--main-featured-color);
                border-radius: 5px;
            }

            .active_feature_false {
                background-color: var(--main-invalid-color);
                border-radius: 5px;
            }

            .number_features {
                max-width: 100%;
            }

            .button {
                margin-top: 10px;
                width: 100%;
            }

            .active_tier {
                background-color: var(--main-featured-color);
            }

            .upgrade-dialog__tier_text {
                display: inline-flex;
                flex-wrap: wrap;
                gap: 5px;
                font-size: 0.75rem;
                line-height: 1rem;
            }

            #infoIcon {
                --mdc-icon-size: 16px;
                margin-left: 3px;
                cursor: pointer;
            }

            .billingbutton {
                --mdc-icon-size: 12px;
                text-align: left;
                border-color: var(--main-primary-color);
                color: var(--main-primary-color);
                background-color: var(--theme-white-color);
            }

            .current_tier {
                font-size: 0.6rem;
                background: var(--main-primary-color);
                color: white;
                padding: 2px;
                border-radius: 3px;
            }

            .upgrade-dialog__tier-name {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                align-items: center;
                gap: 5px;
                font-weight: bold;
            }

            .upgrade-dialog__billing {
                display: flex;
                flex-direction: row;
                gap: 10px;
                justify-content: flex-start;
                margin-top: 1rem;
            }

            .content_header {
                text-align: center;
                text-decoration: underline;
                cursor: pointer;
                background-color: var(--theme-border-color);
                border-radius: 5px;
                margin-bottom: 0.5rem;
            }

            .upgrade-dialog__features_area {
                display: flex;
                flex-direction: column;
                flex-wrap: wrap;
                margin-top: 0.5rem;
                max-height: 200px;
                gap: 5px;
            }

            .upgrade-dialog__demo {
                font-size: 0.75rem;
                line-height: 1rem;
                text-align: center;
            }

            .billingOptions {
                display: flex;
                flex-direction: row;
                justify-content: center;
                width: 100%;
            }

            .active_billing {
                border-color: var(--main-warning-color);
                background-color: var(--main-moon-color);
                color: var(--theme-white-color);
            }

            .billingHolder {
                display: flex;
                flex-direction: row;
                justify-content: center;
                border: 1px solid var(--main-primary-color);
                border-radius: 4px;
                width: 100%;
            }

            .billingButton {
                padding: 0px 0.5rem;
                margin: 2px;
                border-radius: 4px;
                cursor: pointer;
                font-size: 0.6rem;
                flex: 1;
                text-align: center;
            }

            .upgrade-dialog__subscribe {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-end;
                flex: 1 1 0;
            }

            .upgrade-dialog__tiers {
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                gap: 5px;
            }

            .active {
                border-color: var(--main-warning-color);
                color: var(--main-primary-color);
                background-color: var(--theme-white-color);
            }

            .active {
                --btn-border-color: ${unsafeCSS(palette.failureRed)} !important;
            }

            .upgrade-dialog__content {
                padding: 8px 16px;
                overflow-y: auto;
                flex: 1 1 60%;
            }

            .included_text {
                width: 100%;
                display: flex;
                align-items: center;
                flex-direction: row;
            }

            .not_included_area {
                background-color: var(--main-invalid-color);
                color: var(--theme-white-color);
                padding: 2px 10px;
                border-radius: 3px;
                font-size: 0.8rem;
                line-height: 1.5;
                width: 100%;
                text-align: center;
                margin: 0px 20px;
            }

            .included_area {
                background-color: var(--main-featured-color);
                color: var(--theme-white-color);
                padding: 2px;
                border-radius: 3px 3px 0 0;
                font-size: 0.8rem;
                width: 100%;
                text-align: center;
                margin: 0px 20px;
            }

            .left-side-levels {
                display: flex;
                flex-direction: column;
                justify-content: space-between;

                gap: 20px;
                margin-top: 1rem;
            }

            .right-side-exists {
                border-right: 1px solid var(--theme-border-color);
                padding-right: 1rem;
            }

            .title-divider {
                margin: 0;
                border: 0;
                border-top: 1px solid var(--theme-border-color);
            }

            .upgrade-dialog__description {
                color: var(--theme-input-color);
                font-size: 0.75rem;
            }

            .upgrade-dialog__price {
                color: var(--main-primary-color);
                font-size: 2rem;
                font-weight: bold;
            }

            .upgradeImage img {
                width: 100%;
            }

            .link {
                text-decoration: underline;
                cursor: pointer;
            }

            .hidden {
                display: none;
            }

            p {
                text-align: left;
                line-height: 1rem;
                color: var(--main-primary-color)
            }

            .openMore {
                cursor: pointer;
                text-decoration: underline;
                color: var(--main-secondary-color);
            }


            .check {
                vertical-align: middle;
                --mdc-icon-size: 20px;
            }

            .checkout {
                color: white;
                text-decoration: none;
            }

            .actionButtonGroup {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                margin-top: 0.25rem;
                padding-top: 1rem;
                justify-content: space-between;
                border-top: 1px solid var(--main-moon-color);
            }

            @media (min-width: 551px) {
                .desktopOnly {
                    display: block;
                }

                .mobileOnly {
                    display: none;
                }
            }

            @media (max-width: 550px) {
                .desktopOnly {
                    display: none;
                }

                .upgrade-dialog__features_area {
                    max-height: unset;

                }

                .upgrade-dialog__feature_description {
                    width: 100%;
                    max-width: 100%;
                }

                .mobileOnly {
                    display: block;
                }

                .mobileOnly.flexContainer {
                    display: flex;
                }

                .upgradeDialogContent {
                    padding: 2px;
                    justify-content: center;
                }

                .dialogTitle {
                    line-height: normal;
                    font-size: 1rem;
                }
            }
        `;
    }
}
